import React, { useState } from 'react';
import {
  Flex,
  Text,
  Icon,
  FlexProps,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Portal,
  Box,
  Tooltip,
  Editable,
  EditablePreview,
  EditableInput,
} from '@chakra-ui/react';
import { InfoCircle } from '@kula-ai/icons/outline/InfoCircle';
import { CheckboxField } from '@/components/formBuilder/formFields/CheckboxField';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { AlertTriangle } from '@kula-ai/icons/dual-tone/AlertTriangle';
import { KText } from '@/ui-library/components';

interface SelectConfig {
  isChecked: boolean;
  onChange: (_checked: boolean) => void;
}

interface CardTileHeadingProps {
  tileId?: number | string;
  /** Unique id for the checkbox - prevents duplicate ids when multiple tiles share the same label */
  checkboxId?: string;
  label: string;
  hoverLabel?: string;
  metricDefinition?: string;
  basedOn?: string;
  category?: string;
  selectConfig?: SelectConfig;
  containerStyles?: FlexProps;
  truncationLength?: number;
  isStaticReport?: boolean;
  onTileMetaChange?: (_tileId: string | number, _metadata: any) => void;
  currentTitle?: string; // Current title including any changes from ref
  subjectType?: string;
  /** Display labels for applied filters not supported by this block (e.g. ["Time", "Survey name"]) */
  unsupportedFilterLabels?: string[];
  isPrintView?: boolean;
}

export const CardTileHeading: React.FC<CardTileHeadingProps> = ({
  label,
  tileId,
  checkboxId,
  metricDefinition,
  basedOn,
  category,
  selectConfig,
  containerStyles,
  truncationLength,
  isStaticReport = false,
  onTileMetaChange,
  currentTitle,
  subjectType,
  unsupportedFilterLabels,
  isPrintView = false,
}) => {
  const [title, setTitle] = useState(currentTitle || label);
  const showCheckbox = selectConfig;
  const isInlineEdit = Boolean(onTileMetaChange);

  // Derive display title from prop when provided, otherwise use local state (avoids setState in effect)
  const displayTitle = currentTitle !== undefined ? currentTitle : title;

  const renderDefinitionPopover = () => {
    if (isPrintView) return null;
    if (!metricDefinition) return null;

    if (isStaticReport && subjectType === 'live_job_pipeline')
      return (
        <Box
          width="17px"
          height="17px"
          flexShrink={0}
          id="report-metric-definition-icon"
        >
          <Tooltip label={metricDefinition}>
            <span>
              <Icon cursor="pointer" mt="-6px" as={InfoCircle} />
            </span>
          </Tooltip>
        </Box>
      );

    return (
      <Box
        flexShrink={0}
        transition="opacity 0.2s"
        id="report-metric-definition-icon"
      >
        <Popover trigger="hover" placement="top">
          <PopoverTrigger>
            <span>
              <Icon
                cursor="pointer"
                mt={isInlineEdit ? '-10px' : '0'}
                as={InfoCircle}
              />
            </span>
          </PopoverTrigger>
          <Portal>
            <PopoverContent minW="300px" maxW="400px" w="auto">
              <PopoverArrow />
              <PopoverBody p="12.5px 20px">
                <Flex flexDir="column" gap="12px">
                  <Flex flexDir="column">
                    <Text color="secondary-900" size="sm-medium" mr={1}>
                      Metric definition
                    </Text>
                    <Text color="secondary-700" size="sm-regular">
                      {metricDefinition}
                    </Text>
                  </Flex>
                  {basedOn && (
                    <Flex flexDir="column">
                      <Text color="secondary-900" size="sm-medium" mr={1}>
                        Based on
                      </Text>
                      <Text color="secondary-700" size="sm-regular">
                        {basedOn}
                      </Text>
                    </Flex>
                  )}
                  {!isStaticReport && (
                    <Flex flexDir="column">
                      <Text color="secondary-900" size="sm-medium" mr={1}>
                        Category
                      </Text>
                      <Text color="secondary-700" size="sm-regular">
                        {category}
                      </Text>
                    </Flex>
                  )}
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      </Box>
    );
  };

  const renderCheckbox = () => {
    return (
      <CheckboxField
        field={{
          onChange: (e) => selectConfig.onChange(e.target.checked),
          value: selectConfig.isChecked,
        }}
        item={{
          name: checkboxId ?? label,
          label: label,
          labelStyles: { fontSize: '14px' },
          truncationLength: truncationLength || 0,
        }}
      />
    );
  };

  const renderLabel = () => {
    return (
      <Flex minWidth={0} maxWidth="100%" gap="5px" alignItems="center">
        <Box minWidth={0} maxWidth="100%" width="100%">
          {isInlineEdit ? (
            <Editable
              p="0"
              maxWidth="100%"
              value={displayTitle}
              selectAllOnFocus={false}
              onChange={(value) => {
                // Limit to 150 characters
                if (value.length <= 250) {
                  setTitle(value);
                }
              }}
              onSubmit={(value) => {
                // If value is empty, reset to original title
                const finalValue = value.trim() === '' ? label : value;
                setTitle(finalValue);
                onTileMetaChange?.(tileId, {
                  title: finalValue,
                });
              }}
              onCancel={() => {
                // Reset to original value on cancel
                setTitle(label);
              }}
            >
              <TruncatedTextWithTooltip
                as={EditablePreview}
                toolTipVariant="default"
                text={displayTitle}
                placement="top"
                size="md-medium"
                color="secondary-900"
                fontSize="sm"
                fontWeight="semiBold"
                maxWidth="100%"
                userSelect="none"
                cursor="pointer"
                p="0 4px"
                mt="3px"
                _hover={{
                  bg: 'secondary-50',
                }}
              />
              <EditableInput
                border="1px solid #E5E5E5"
                px="4px"
                borderRadius="6px"
                py="2px"
                _focus={{
                  outline: 'none !important',
                  boxShadow: 'none !important',
                }}
                color="secondary-900"
                fontSize="sm"
                fontWeight="semiBold"
              />
            </Editable>
          ) : isPrintView ? (
            <Text
              size="md-medium"
              color="secondary-900"
              fontSize="sm"
              mt="3px"
              fontWeight="semiBold"
              userSelect="none"
              wordBreak="break-word"
              whiteSpace="normal"
              overflowWrap="break-word"
              display="block"
            >
              {displayTitle}
            </Text>
          ) : (
            <TruncatedTextWithTooltip
              toolTipVariant="default"
              text={displayTitle}
              placement="top"
              size="md-medium"
              color="secondary-900"
              fontSize="sm"
              mt="3px"
              fontWeight="semiBold"
              userSelect="none"
            />
          )}
        </Box>
        {renderDefinitionPopover()}
      </Flex>
    );
  };

  const hasUnsupportedFilters =
    Array.isArray(unsupportedFilterLabels) &&
    unsupportedFilterLabels.length > 0;

  return (
    <Flex flexDir="column" {...containerStyles}>
      <Flex gap="12px" mt="2px" alignItems="flex-start">
        <Flex
          alignItems="flex-start"
          minWidth={0}
          maxWidth={metricDefinition ? '95%' : '100%'}
        >
          {showCheckbox && renderCheckbox()}
          {!showCheckbox && renderLabel()}
        </Flex>
      </Flex>
      {hasUnsupportedFilters && !isStaticReport && (
        <Flex alignItems="center" gap="6px">
          <Tooltip
            label={`Filters not applicable to this block: ${unsupportedFilterLabels.join(', ')}`}
          >
            <Box>
              <Icon
                as={AlertTriangle}
                boxSize="14px"
                color="pastel-1-dark"
                flexShrink={0}
              />
            </Box>
          </Tooltip>
          <KText size="xs-medium" noOfLines={1}>
            N/A:
            <KText size="xs-regular" noOfLines={1} display="inline">
              {unsupportedFilterLabels.join(', ')}
            </KText>
          </KText>
        </Flex>
      )}
    </Flex>
  );
};
